    <template>
        <div class="shopping-cart">
            <div class="cart-icon" @click="toggleCart">
                <i class="fas fa-shopping-cart"></i>
                <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
            </div>

            <div v-if="isCartOpen" class="cart-dropdown">
                <div class="cart-header">
                    <h3>Carrinho de Compras</h3>
                    <button @click="toggleCart" class="close-btn">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <div v-if="cartItems.length === 0" class="cart-empty">
                    <i class="fas fa-shopping-cart"></i>
                    <p>Seu carrinho está vazio</p>
                </div>

                <div v-else class="cart-items">
                    <div v-for="item in cartItems" :key="getItemKey(item)" class="cart-item">
                        <img :src="item.url_imagem || require('@/assets/background/capa-padrao.png')"
                            :alt="item.nome_disciplina">
                        <div class="item-info">
                            <h4>{{ item.nome_disciplina }}</h4>
                            <p class="item-type" v-if="item.tipo">{{ formatItemType(item.tipo) }}</p>
                            <p class="item-price">R$ {{ formatPrice(item.preco) }}</p>
                        </div>
                        <button @click="handleRemoveFromCart(item)" class="remove-btn">
                            <i class="fas fa-trash"></i>
                        </button>
                    </div>
                </div>

                <div v-if="cartItems.length > 0" class="cart-footer">
                    <div class="cart-total">
                        <span>Total:</span>
                        <span class="total-price">R$ {{ totalPrice }}</span>
                    </div>
                    <button @click="checkout" class="checkout-btn">
                        Finalizar Compra
                    </button>
                </div>
            </div>

            <!-- Toast de notificação -->
            <div v-if="showToast" class="toast-notification" :class="toastType">
                <i :class="toastIcon"></i>
                <span>{{ toastMessage }}</span>
            </div>
        </div>
    </template>

<script>
import requestV2 from '@/router/requestV2.js';

export default {
    name: 'ShoppingCart',
    data() {
        return {
            isCartOpen: false,
            cartItems: [],
            showToast: false,
            toastMessage: '',
            toastType: 'success'
        };
    },
    computed: {
        cartCount() {
            return this.cartItems.length;
        },
        totalPrice() {
            const total = this.cartItems.reduce((sum, item) => {
                const price = parseFloat(item.preco) || 0;
                return sum + price;
            }, 0);
            return total.toFixed(2).replace('.', ',');
        },
        toastIcon() {
            return this.toastType === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle';
        }
    },
    methods: {
        toggleCart() {
            this.isCartOpen = !this.isCartOpen;
        },
        getItemKey(item) {
            if (item.isPacote || item.tipo === 'pacote-completo') {
                return `pacote-${item.codigo_disciplina}`;
            }
            return `${item.codigo_disciplina}-${item.tipo || 'individual'}`;
        },
        formatItemType(tipo) {
            const tipos = {
                'material-didatico': 'Material Didático',
                'video-aula': 'Vídeo Aula',
                'atividade': 'Atividade',
                'pacote-completo': 'Pacote Completo',
                'pacote': 'Pacote Completo'
            };
            return tipos[tipo] || tipo;
        },
        formatPrice(preco) {
            if (!preco) return '0,00';
            return parseFloat(preco).toFixed(2).replace('.', ',');
        },
        async loadCart() {
            try {
                const response = await requestV2.get('/cart');
                this.cartItems = response.data;
            } catch (e) {
                console.error('Erro ao carregar carrinho da API:', e);
                // Fallback para não quebrar a tela
                this.cartItems = [];
            }
        },
        async addToCart(item) {
            console.log("Tentando adicionar ao carrinho:", item);

            const itemWithDate = {
                ...item,
                addedAt: new Date().toISOString()
            };

            const isPacoteCompleto = item.isPacote || item.tipo === 'pacote-completo';
            const existePacote = this.cartItems.some(i =>
                (i.isPacote || i.tipo === 'pacote-completo') &&
                i.codigo_disciplina === item.codigo_disciplina
            );
            const itensIndividuais = this.cartItems.filter(i =>
                i.codigo_disciplina === item.codigo_disciplina &&
                !i.isPacote &&
                i.tipo !== 'pacote-completo'
            );

            if (isPacoteCompleto) {
                if (existePacote) {
                    this.showNotification('O pacote completo já está no carrinho!', 'warning');
                    return;
                }
                if (itensIndividuais.length > 0) {
                    this.showNotification('Você já possui itens individuais desta disciplina no carrinho. Remova-os primeiro para adicionar o pacote completo.', 'warning');
                    return;
                }
            } else {
                if (existePacote) {
                    this.showNotification('O pacote completo desta disciplina já está no carrinho!', 'warning');
                    return;
                }
                const existeTipo = itensIndividuais.some(i => i.tipo === item.tipo);
                if (existeTipo) {
                    this.showNotification('Este item já está no carrinho!', 'warning');
                    return;
                }
                if (itensIndividuais.length >= 3) {
                    this.showNotification('Você já possui todos os 3 tipos de conteúdo no carrinho. Considere adquirir o pacote completo!', 'warning');
                    return;
                }
            }

            try {
                await requestV2.post('/cart', {
                    item_key: this.getItemKey(item),
                    item_data: itemWithDate
                });
                await this.loadCart();
                this.showNotification('Item adicionado ao carrinho!', 'success');

                // Notifica outras abas/componentes (Custom Event em vez de Storage Event)
                window.dispatchEvent(new Event('cart-updated'));
            } catch (e) {
                console.error('Erro ao adicionar item:', e);
                this.showNotification('Erro ao adicionar item ao carrinho.', 'error');
            }
        },
        async handleRemoveFromCart(item) {
            try {
                await requestV2.delete(`/cart/${this.getItemKey(item)}`);
                await this.loadCart();
                this.showNotification('Item removido do carrinho!', 'success');

                window.dispatchEvent(new Event('cart-updated'));
            } catch (e) {
                console.error('Erro ao remover item:', e);
            }
        },
        checkout() {
            this.$router.push('/mycart');
            this.isCartOpen = false;
        },
        showNotification(message, type = 'success') {
            this.toastMessage = message;
            this.toastType = type;
            this.showToast = true;

            setTimeout(() => {
                this.showToast = false;
            }, 3000);
        }
    },
    async mounted() {
        await this.loadCart();

        // Escuta eventos customizados de atualização do carrinho
        window.addEventListener('cart-updated', this.loadCart);
    },
    beforeUnmount() {
        window.removeEventListener('cart-updated', this.loadCart);
    }
};
</script>

<style scoped>
.shopping-cart {
    position: relative;
}

.cart-icon {
    position: relative;
    cursor: pointer;
    color: #FEC401;
    font-size: 24px;
    padding: 8px;
    transition: color 0.3s ease;
}

.cart-icon:hover {
    color: #ffffff;
}

.cart-badge {
    position: absolute;
    top: 0;
    right: 0;
    background-color: #ff0000;
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
}

.cart-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    width: 350px;
    max-height: 500px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
}

.cart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid #eee;
}

.cart-header h3 {
    margin: 0;
    color: #09105D;
    font-size: 18px;
}

.close-btn {
    background: none;
    border: none;
    font-size: 20px;
    color: #666;
    cursor: pointer;
    padding: 0;
}

.close-btn:hover {
    color: #000;
}

.cart-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #999;
}

.cart-empty i {
    font-size: 48px;
    margin-bottom: 15px;
}

.cart-items {
    flex: 1;
    overflow-y: auto;
    padding: 15px;
}

.cart-item {
    display: flex;
    gap: 10px;
    padding: 10px;
    border-bottom: 1px solid #eee;
    align-items: center;
}

.cart-item:last-child {
    border-bottom: none;
}

.cart-item img {
    width: 60px;
    height: 80px;
    object-fit: cover;
    border-radius: 4px;
}

.item-info {
    flex: 1;
}

.item-info h4 {
    margin: 0 0 5px 0;
    font-size: 14px;
    color: #333;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.item-type {
    margin: 0 0 5px 0;
    font-size: 12px;
    color: #666;
    font-style: italic;
}

.item-price {
    margin: 0;
    color: #5DADE2;
    font-weight: 600;
}

.remove-btn {
    background: none;
    border: none;
    color: #ff0000;
    cursor: pointer;
    padding: 5px;
    font-size: 16px;
}

.remove-btn:hover {
    color: #cc0000;
}

.cart-footer {
    border-top: 1px solid #eee;
    padding: 15px;
}

.cart-total {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.total-price {
    color: #5DADE2;
    font-size: 18px;
}

.checkout-btn {
    width: 100%;
    background-color: #FEC401;
    color: #09105D;
    border: none;
    padding: 12px;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.checkout-btn:hover {
    background-color: #e5b001;
}

/* Toast Notification */
.toast-notification {
    position: fixed;
    bottom: 30px;
    right: 30px;
    background-color: white;
    padding: 15px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 10000;
    animation: slideIn 0.3s ease;
    min-width: 300px;
    max-width: 400px;
}

.toast-notification.success {
    border-left: 4px solid #28a745;
}

.toast-notification.success i {
    color: #28a745;
}

.toast-notification.warning {
    border-left: 4px solid #ffc107;
}

.toast-notification.warning i {
    color: #ffc107;
}

.toast-notification i {
    font-size: 20px;
}

.toast-notification span {
    flex: 1;
    color: #333;
    font-size: 14px;
}

@keyframes slideIn {
    from {
        transform: translateX(400px);
        opacity: 0;
    }

    to {
        transform: translateX(0);
        opacity: 1;
    }
}

@media (max-width: 480px) {
    .cart-dropdown {
        width: 300px;
        right: -20px;
    }

    .toast-notification {
        bottom: 20px;
        right: 20px;
        left: 20px;
        min-width: auto;
    }

    .close-btn .fa-times {
        font-size: 13px;
    }

    .fa-solid,
    .fas {
        font-size: 20px;
    }

    .cart-icon {
        padding: 2px;
    }
}
</style>